import { useEffect, useRef, useState } from 'react';
import { fetchMe } from '@/lib/session.js';
import { mineFor } from '@/lib/mine.js';
import { crmDeepLink } from '@/lib/links.js';
import { Input } from '@/components/ui/input.jsx';
import { fmtCents } from '@/components/money/format.js';
import { getCommissions } from '@/services/commissions.service.js';

// COMMISSIONS (deals-door-1 T18, PLAN-FINAL 4.3; answers round 2, Q3-A to Q3-E, David 2026-10-28). A line is payable when the
// agency's commission on it has ARRIVED (agency bill: the line CLOSED, signed paperwork AND money) and the ledger states it;
// a bind and an endorsement that adds premium are positive lines; a return is a CHARGEBACK line naming the line it reverses.
// The server decides every word, number and count here (services/commission-view.service.js through
// GET /api/tracon/commissions); this page renders them and derives nothing: no closed rule, no sum, no date arithmetic. The
// closed-on range is sent as the months typed. Every colour has its status word beside it. Unknown commission or producer
// reads "Not stated", never $0.00; the producer's share reads the server's word ("share not stated"), else the server's share
// as a percent and the producer amount it gives (round 3, R2c-Q1: the page multiplies nothing).
// Default filter: Mine for a personal sign-in, All for the shared login (it is not a person) or when /auth/me could not
// be read (never assumed personal). A failed read shows the server's sentence and no view, never an empty one.

// One label and one tone per server status word (the keys ARE the service's STATUS_WORDS; guard
// tests/commission-page.test.js P11). A word the page does not know is shown as sent, in the neutral tone.
export const STATUS_LABEL = Object.freeze({
  payable: { label: 'Payable', tone: 'success' },
  closed_commission_not_stated: { label: 'Closed, commission not stated', tone: 'warning' },
  waiting_paperwork: { label: 'Waiting for signed paperwork', tone: 'info' },
  waiting_money: { label: 'Waiting for money', tone: 'info' },
  waiting_money_day: { label: 'Waiting for the money day', tone: 'info' },
  waiting_paperwork_and_money: { label: 'Waiting for paperwork and money', tone: 'info' },
  ambiguous_term: { label: 'More than one live bind on this deal', tone: 'warning' },
  charged_back: { label: 'Charged back', tone: 'danger' },
  waiting_return_premium: { label: 'Return: waiting on the carrier\'s return premium', tone: 'info' },
  chargeback_commission_not_stated: { label: 'Return priced, commission not stated', tone: 'warning' },
  no_chargeback_not_arrived: { label: 'No chargeback: the commission never arrived', tone: 'neutral' },
  chargeback_original_unclear: { label: 'Return: no single live bind to reverse', tone: 'warning' },
  return_unsigned: { label: 'Return not signed yet', tone: 'info' },
});
const TONE_CLASS = { success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger', info: 'bg-info', neutral: 'bg-text-dim' };

const SCOPES = [
  { word: 'me', label: 'Mine' },
  { word: 'unassigned', label: 'Unassigned' },
  { word: 'all', label: 'All' },
];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function StatusChip({ word }) {
  const known = STATUS_LABEL[word];
  const tone = known ? known.tone : 'neutral';
  return (
    <span data-status-word={word} className="inline-flex items-center gap-1.5 text-xs text-text">
      <span data-status-tone={tone} aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-pill ${TONE_CLASS[tone]}`} />
      <span>{known ? known.label : word}</span>
    </span>
  );
}

function producerWords(r) {
  if (!r.producer_user_id) return 'Not stated';
  return r.producer_label || 'unnamed user';
}

const LINE_WORD = { bind: 'bind', endorsement: 'endorsement', chargeback: 'chargeback' };

function amountWords(r) {
  if (r.commission !== null && r.commission !== undefined) return fmtCents(r.commission);
  return r.waiting_word || 'Not stated';
}

function CommissionRow({ r }) {
  const chargeback = r.line_type === 'chargeback';
  return (
    <li data-commission-row={r.transaction_id} className="py-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <StatusChip word={r.status} />
        <span data-commission-line className="text-xs text-text-dim">{LINE_WORD[r.line_type] || r.type || 'line'}</span>
        {r.crm_deal_id
          ? <a href={crmDeepLink('Deals', r.crm_deal_id)} target="_blank" rel="noreferrer" className="font-mono text-xs text-text-muted hover:underline">deal {r.crm_deal_id}</a>
          : <span className="font-mono text-xs text-text-dim">no deal id</span>}
        <span data-commission-producer className="text-xs text-text-muted">producer: {producerWords(r)}</span>
        {chargeback
          ? <span className="text-xs text-text-dim">{r.type} effective {r.chargeback_on || 'not stated'}{r.reverses ? <>, reverses {r.reverses.type} <span className="font-mono">{String(r.reverses.transaction_id).slice(0, 8)}</span></> : ''}</span>
          : <span className="text-xs text-text-dim">term {r.effective_date || 'not stated'} to {r.end_date || 'not stated'}</span>}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11.5px] text-text-muted">
        <span>commission <span data-commission-amount className="text-text">{amountWords(r)}</span></span>
        <span data-commission-share>producer share {r.producer_share === null || r.producer_share === undefined ? (r.producer_share_word || 'not stated') : `${Math.round(r.producer_share * 1e6) / 1e4}%${r.producer_share_source === 'policy_override' ? ' (this policy)' : (r.term_kind ? ` (${r.term_kind === 'renewal' ? 'renewal' : 'new business'} rate)` : '')}`}</span>
        {r.producer_amount !== null && r.producer_amount !== undefined ? <span data-commission-producer-amount>producer <span className="text-text">{fmtCents(r.producer_amount)}</span></span> : null}
        {chargeback && r.producer_share_basis_word ? <span data-commission-share-basis className="text-text-dim">{r.producer_share_basis_word}</span> : null}
        <span>premium {fmtCents(r.premium_delta)}</span>
        {chargeback ? null : <span>{r.closed_on ? `closed ${r.closed_on}` : 'not closed'}</span>}
        {chargeback ? null : <span>signed {r.signed_on || 'no day'}</span>}
        {chargeback ? null : <span>money {r.money_on || 'no day'}</span>}
        {r.line_type === 'bind' ? <span>platform fee {fmtCents(r.platform_fee)}</span> : null}
        {r.line_type === 'bind' ? <span>agency fee {fmtCents(r.agency_fee)}</span> : null}
        <span>{r.co_brokered === true ? 'co-brokered' : (r.co_brokered === false ? 'not co-brokered' : 'co-brokered: not stated')}</span>
        {r.cancellation ? <span className="text-danger">cancellation effective {r.cancellation.effective_date || 'not stated'}</span> : null}
      </div>
    </li>
  );
}

function Totals({ totals }) {
  const pay = totals.payable || { count: 0, commission: 0 };
  const wait = totals.waiting || { count: 0, commission: 0, commission_not_stated: 0 };
  const cb = totals.charged_back || { count: 0, commission_returned: 0, producer_amount: null, share_not_stated: 0 };
  return (
    <div data-commission-totals className="mt-4 rounded-card border border-border bg-surface p-4 text-sm text-text-muted">
      <div>
        {pay.count} payable, commission <span className="font-mono text-text">{fmtCents(pay.commission)}</span>
        {pay.producer_amount === null || pay.producer_amount === undefined ? `; producer share not stated on ${pay.share_not_stated ?? pay.count}` : <>; producer <span data-commission-payable-producer className="font-mono text-text">{fmtCents(pay.producer_amount)}</span>{pay.share_not_stated ? `, share not stated on ${pay.share_not_stated}` : ''}</>}
      </div>
      <div className="mt-1">
        {wait.count} waiting (paperwork or money), commission <span className="font-mono">{fmtCents(wait.commission)}</span>{wait.commission_not_stated ? `, ${wait.commission_not_stated} not stated` : ''}
      </div>
      <div className="mt-1">
        {cb.count} charged back, commission returned <span className="font-mono">{fmtCents(cb.commission_returned)}</span>
        {cb.producer_amount === null || cb.producer_amount === undefined ? `; producer share not stated on ${cb.share_not_stated}` : <>; producer <span className="font-mono">{fmtCents(cb.producer_amount)}</span>{cb.share_not_stated ? `, share not stated on ${cb.share_not_stated}` : ''}</>}
      </div>
      <div className="mt-1">
        Beside them, not in those totals: {totals.closed_commission_not_stated || 0} closed, commission not stated; {plural(totals.waiting_return_premium || 0, 'return', 'returns')} waiting on the carrier's return premium; {plural(totals.no_chargeback_not_arrived || 0, 'return', 'returns')} with no chargeback (the commission never arrived)
      </div>
    </div>
  );
}

function ExcludedLine({ excluded, filtered }) {
  const ex = excluded || { count: 0, no_policy_link: { count: 0 }, policy_not_found: { count: 0 } };
  return (
    <div className="mt-3 space-y-1 text-xs text-text-muted">
      <p data-commission-excluded>
        {plural(ex.count, 'live bind', 'live binds')} not shown
        {ex.count
          ? `: ${ex.no_policy_link.count} with no policy link, ${ex.policy_not_found.count} whose policy TRACON does not hold.`
          : ': every live bind joined a policy.'}
      </p>
      {ex.lines ? (
        <p data-commission-excluded-lines>
          {plural(ex.lines.count || 0, 'endorsement or return line', 'endorsement or return lines')} not shown: {ex.lines.no_live_bind ? ex.lines.no_live_bind.count : 0} on a deal with no live bind, {ex.lines.bind_not_shown ? ex.lines.bind_not_shown.count : 0} whose bind is not shown.
        </p>
      ) : null}
      {filtered ? (
        <p data-commission-filtered>
          Left out by the filters: {filtered.by_producer} by producer, {filtered.by_closed_range} by closed-on month.
        </p>
      ) : null}
    </div>
  );
}

export default function MoneyCommissions() {
  // who: null while /auth/me is asked; then lib/mine mineFor: { personal, kind, sentence }. kind null: it could not be read.
  const [who, setWho] = useState(null);
  const [scope, setScope] = useState(null);
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    let live = true;
    Promise.resolve(fetchMe()).catch(() => null).then((me) => {
      if (!live) return;
      const mine = mineFor(me);
      setWho(mine);
      setScope(mine.personal ? 'me' : 'all');
    });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!scope) return;
    const mine = ++seq.current;
    setView(null);
    setError('');
    getCommissions({ producer: scope, closedFromMonth: fromMonth || null, closedToMonth: toMonth || null })
      .then((d) => { if (mine === seq.current) setView(d); })
      .catch((e) => { if (mine === seq.current) setError(e?.body?.message || e?.message || 'Could not read the commission view.'); });
  }, [scope, fromMonth, toMonth]);

  const mineOff = who && !who.personal;
  const mineSentence = who ? who.sentence : '';
  const rows = view && Array.isArray(view.rows) ? view.rows : [];

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Commissions</h2>
        <p className="mt-1 text-sm text-text-muted">
          Commission is payable when it has arrived: the line closed, the signed paperwork AND the money both in. Bound alone is never payable. When money goes back out, the commission returned is charged back on its own line.
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-1" role="group" aria-label="Producer">
          {SCOPES.map(({ word, label }) => (
            <button
              key={word}
              type="button"
              data-commission-scope={word}
              aria-pressed={scope === word ? 'true' : 'false'}
              disabled={!who || (word === 'me' && mineOff)}
              title={word === 'me' && mineOff ? mineSentence : undefined}
              onClick={() => setScope(word)}
              className={[
                'min-h-[40px] rounded-card border px-3 text-sm',
                scope === word ? 'border-accent bg-accent text-white' : 'border-border text-text-muted hover:text-text',
                'disabled:cursor-not-allowed disabled:opacity-50',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="text-xs text-text-muted">
          Closed on, from
          <Input type="month" aria-label="Closed on, from month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="mt-1 w-40" />
        </label>
        <label className="text-xs text-text-muted">
          to
          <Input type="month" aria-label="Closed on, to month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className="mt-1 w-40" />
        </label>
      </div>
      {mineSentence ? <p className="mt-2 text-xs text-text-muted">{mineSentence}</p> : null}

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {!error && !view ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}
      {!error && view ? (
        <>
          <Totals totals={view.totals || {}} />
          <ExcludedLine excluded={view.excluded} filtered={view.filtered} />
          {rows.length === 0 ? <p className="mt-4 text-sm text-text-muted">No commission line matches these filters.</p> : (
            <ul className="mt-3 divide-y divide-border rounded-card border border-border bg-surface px-3">
              {rows.map((r) => <CommissionRow key={r.transaction_id} r={r} />)}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}
